import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Laptop, CheckCircle2, X, Sparkles, Share2, PlusSquare, ShieldCheck } from 'lucide-react';

export default function PwaInstallPrompt({ isOpen, onClose, triggerManual }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showAutoBanner, setShowAutoBanner] = useState(false);

  useEffect(() => {
    // Detect OS
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    const isAndroidDevice = /android/.test(ua);
    setIsIOS(isIosDevice);
    setIsAndroid(isAndroidDevice);

    // Check if already in standalone (PWA installed) mode
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      // Prevent standard browser banner to show our custom cyberpunk UI
      e.preventDefault();
      setDeferredPrompt(e);
      
      const dismissed = localStorage.getItem('pwa_prompt_dismissed');
      // If not previously dismissed or manual trigger, show banner
      if (!dismissed) {
        setShowAutoBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowAutoBanner(false);
      localStorage.setItem('pwa_installed', 'true');
    });

    // Check if on mobile and not installed, show helper banner after 3 seconds if not dismissed
    const timer = setTimeout(() => {
      const dismissed = localStorage.getItem('pwa_prompt_dismissed');
      if (!dismissed && !isInstalled && (isIosDevice || isAndroidDevice)) {
        setShowAutoBanner(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowAutoBanner(false);
        if (onClose) onClose();
      }
      setDeferredPrompt(null);
    } else {
      // Open detailed instructions modal if native prompt is unavailable
      if (triggerManual) triggerManual();
    }
  };

  const handleDismissBanner = () => {
    setShowAutoBanner(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  const isModalVisible = isOpen;

  return (
    <>
      {/* ── FLOATING QUICK INSTALL BANNER (BOTTOM-RIGHT) ── */}
      {showAutoBanner && !isInstalled && !isModalVisible && (
        <div
          className="animate-fade"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            maxWidth: '380px',
            width: 'calc(100% - 48px)',
            background: 'linear-gradient(135deg, rgba(14, 18, 26, 0.96) 0%, rgba(20, 15, 35, 0.98) 100%)',
            border: '1px solid var(--neon-green)',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 255, 135, 0.25), 0 0 20px rgba(0, 0, 0, 0.8)',
            padding: '16px 20px',
            zIndex: 9999,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #00FF87, #00C8FF)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#040A09',
                  fontWeight: 900,
                  fontSize: '1.2rem',
                  flexShrink: 0,
                  boxShadow: '0 0 15px rgba(0, 255, 135, 0.4)',
                }}
              >
                ⚡
              </div>
              <div>
                <div style={{ fontSize: '0.92rem', fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.01em' }}>
                  Install RAPMAP Shortcut
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  1-Tap desktop / home screen quick launch
                </div>
              </div>
            </div>

            <button
              onClick={handleDismissBanner}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            <button
              onClick={deferredPrompt ? handleInstallClick : triggerManual}
              style={{
                flex: 1,
                padding: '9px 14px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #00FF87 0%, #00C8FF 100%)',
                color: '#050D0A',
                fontWeight: 900,
                fontSize: '0.82rem',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 0 15px rgba(0, 255, 135, 0.3)',
              }}
            >
              <Download size={14} /> Install Now
            </button>
            <button
              onClick={handleDismissBanner}
              style={{
                padding: '9px 12px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.08)',
                color: 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: '0.8rem',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                cursor: 'pointer',
              }}
            >
              Later
            </button>
          </div>
        </div>
      )}

      {/* ── FULL SCREEN INTERACTIVE INSTALL MODAL ── */}
      {isModalVisible && (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
          <div
            className="modal-content animate-scale"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '540px',
              width: '100%',
              background: 'linear-gradient(145deg, #0C0E14 0%, #120E22 100%)',
              border: '1px solid var(--neon-green)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.85), 0 0 40px rgba(0, 255, 135, 0.25)',
              padding: '28px',
              borderRadius: '20px',
              position: 'relative',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: 'rgba(0, 255, 135, 0.15)',
                    border: '1px solid var(--neon-green)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--neon-green)',
                  }}
                >
                  <Download size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 900, color: '#FFFFFF', margin: 0 }}>
                    Install Admin Shortcut
                  </h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
                    Add RAPMAP Console to Home Screen / Desktop App
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'var(--text-muted)',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* App Preview Card */}
            <div
              style={{
                padding: '16px',
                borderRadius: '14px',
                background: 'rgba(0, 0, 0, 0.45)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #00FF87 0%, #7928CA 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.6rem',
                  boxShadow: '0 0 20px rgba(0, 255, 135, 0.35)',
                  flexShrink: 0,
                }}
              >
                👑
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1rem', fontWeight: 900, color: '#FFFFFF' }}>RAPMAP Admin Portal</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--neon-green)', fontWeight: 700 }}>
                  v2.6 Enterprise Progressive Web App
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Full offline support, instant boot, zero browser URL bar distractions.
                </div>
              </div>
            </div>

            {/* Direct Browser Install Button if Event Available */}
            {deferredPrompt ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                <button
                  onClick={handleInstallClick}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #00FF87 0%, #00C8FF 100%)',
                    color: '#050D0A',
                    fontWeight: 900,
                    fontSize: '0.95rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    boxShadow: '0 0 25px rgba(0, 255, 135, 0.4)',
                  }}
                >
                  <Download size={18} /> 1-Click Install App Shortcut
                </button>
                <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  A native system prompt will appear on your screen. Click "Install" or "Add".
                </div>
              </div>
            ) : isInstalled ? (
              <div
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'rgba(0, 255, 135, 0.12)',
                  border: '1px solid rgba(0, 255, 135, 0.4)',
                  color: 'var(--neon-green)',
                  fontSize: '0.88rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '20px',
                }}
              >
                <CheckCircle2 size={20} />
                <span>RAPMAP Admin Shortcut is already installed and active on this device!</span>
              </div>
            ) : (
              /* Step-by-step instructions for iOS, Android, or Desktop */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--neon-cyan)', letterSpacing: '0.04em' }}>
                  {isIOS ? '📱 QUICK STEPS FOR IPHONE / IPAD (SAFARI):' : isAndroid ? '🤖 QUICK STEPS FOR ANDROID (CHROME / BRAVE):' : '💻 QUICK STEPS FOR DESKTOP (CHROME / EDGE):'}
                </div>

                {isIOS ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-purple)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>1</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Tap the <strong>Share button</strong> ( <Share2 size={13} style={{ display: 'inline', color: 'var(--neon-cyan)' }} /> ) in Safari bottom toolbar.
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-purple)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>2</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Scroll down and tap <strong>"Add to Home Screen"</strong> ( <PlusSquare size={13} style={{ display: 'inline', color: 'var(--neon-green)' }} /> ).
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-purple)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>3</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Tap <strong>Add</strong> at top-right. Your RAPMAP Admin icon is ready!
                      </span>
                    </div>
                  </div>
                ) : isAndroid ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-cyan)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>1</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Tap the <strong>three dots menu (⋮)</strong> in Chrome at top-right.
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-cyan)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>2</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-green)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>1</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Look for the <strong>Install (⊕ or ⬇)</strong> button at the right end of the browser URL search bar.
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--neon-green)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem' }}>2</span>
                      <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                        Click <strong>"Install"</strong> to launch RAPMAP in dedicated desktop standalone window mode!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Benefits of Installing */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <Sparkles size={14} style={{ color: 'var(--neon-green)' }} /> 1-Tap Quick Launch
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <ShieldCheck size={14} style={{ color: 'var(--neon-cyan)' }} /> Secure Admin Session
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <Laptop size={14} style={{ color: 'var(--neon-purple)' }} /> Desktop & Mobile Sync
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <Smartphone size={14} style={{ color: 'var(--neon-amber)' }} /> Zero App Store Downloads
              </div>
            </div>

            {/* Close / Understood Button */}
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              style={{ width: '100%', marginTop: '20px', padding: '10px', borderRadius: '10px' }}
            >
              Got It / Close Window
            </button>
          </div>
        </div>
      )}
    </>
  );
}
